/**
 * AuthenticatedImage — fetcha image via apiClient (Bearer JWT) e exibe via blob URL.
 *
 * Por que isso existe:
 * - Endpoints como /api/jobs/{id}/evidencias/{eid}/file precisam de autenticacao JWT
 * - Tag <img> NAO suporta header Authorization
 * - Solucao anterior usava ?token=<jwt> mas isso vaza em logs/Referer/history
 * - Solucao definitiva: fetchar via apiClient (header Authorization OK), criar
 *   ObjectURL local com o blob e usar no <img src>
 *
 * Vantagens vs ?token=<jwt>:
 * - Token NUNCA aparece na URL → nao vaza em logs
 * - Token NUNCA aparece no Referer header
 * - Token NUNCA fica no browser history
 * - Browser nao cacheia indefinidamente (blob URL eh ephemeral)
 *
 * Uso:
 *   <AuthenticatedImage src="/api/jobs/637/evidencias/123/file" alt="..." />
 *
 * Comportamento:
 * - Mostra placeholder/spinner durante o fetch
 * - Mostra error icon se 401/404/500
 * - Revoga blob URL no unmount (evita memory leak)
 */
import React, { useEffect, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import apiClient from '../services/apiClient';

interface AuthenticatedImageProps {
  src: string | null | undefined;
  alt?: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  fallback?: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
}

export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt = '',
  className = '',
  onLoad,
  onError,
  fallback,
  style,
  onClick,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setBlobUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    setLoading(true);
    setError(false);

    // URL absoluta externa: usar direto sem fetch (CDN, etc)
    if (src.startsWith('http://') || src.startsWith('https://')) {
      setBlobUrl(src);
      setLoading(false);
      return;
    }

    // URL relativa autenticada: fetch via apiClient (manda Bearer header)
    // Strip /api do inicio se presente — apiClient ja tem baseURL com /api
    const urlForApi = src.startsWith('/api/') ? src.slice(4) : src;

    apiClient
      .get(urlForApi, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        const blob = response.data as Blob;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setLoading(false);
        onLoad?.();
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[AuthenticatedImage] fetch failed:', src, err);
        setError(true);
        setLoading(false);
        onError?.();
      });

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !blobUrl) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className={`flex flex-col items-center justify-center text-gray-400 ${className}`}>
        <ImageOff className="w-8 h-8 mb-1" />
        <span className="text-xs">Erro ao carregar</span>
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      onError={() => {
        setError(true);
        onError?.();
      }}
    />
  );
};

export default AuthenticatedImage;
